import { ReactElement, useRef } from 'react';
import { darken, Fade, IconButton, makeStyles } from '@material-ui/core';
import ArrowLeftIcon from '@eduzz/houston-icons/ChevronLeft';
import TrashIcon from '@eduzz/houston-icons/TrashSolid';
import { useGlobalSearch } from 'hooks/globalSearch';
import { useAppSelector } from 'hooks/redux';
import { useDispatch } from 'react-redux';
import { toggleInputFadeAnimation } from 'redux/globalSearch/slice';
import { withCourseTheme } from 'decorators/withCourseTheme';
import { CourseTheme } from 'assets/themes/Course';
import { useCustomization } from 'hooks/customization';

interface IProps {
  children: ReactElement;
}

interface StyleProps {
  background: string;
}

const useStyles = makeStyles<CourseTheme, StyleProps>((theme) => ({
  backdrop: {
    background: (props) => props?.background ?? theme.variables.courseTheme.background,
    position: 'absolute',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'calc(100% - 16px)',
    maxWidth: 'calc(100% - 16px)',
    '@media (min-width: 426px)': {
      width: 'calc(100% - 32px)',
      maxWidth: 'calc(100% - 32px)',
    },
  },
  inputContainer: {
    background: (props) =>
      props?.background ? darken(props.background, 0.3) : theme.variables.courseTheme.cardBackground,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    height: 35,
    '& button': {
      padding: theme.spacing(1),
    },
  },
  input: {
    color: theme.variables.courseTheme.text,
    background: 'transparent',
    width: 'calc(100% - 32px)',
    borderWidth: '0px !important',
    paddingLeft: '0px !important',
    margin: '0px !important',
    height: '35px !important',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none !important',
    },
  },
}));

const SearchInputContainer = (props: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const input = useAppSelector((store) => store.globalSearch.headerInput);
  const { onSearchChange } = useGlobalSearch();
  const dispatch = useDispatch();
  const customization = useCustomization();
  const classes = useStyles({
    background: customization.header_background_color,
  });

  const closeSearchField = () => {
    clearInput();
    dispatch(toggleInputFadeAnimation());
  };

  const clearInput = () => {
    if (!inputRef.current) return;
    inputRef.current.value = '';
    onSearchChange('');
  };

  return (
    <>
      <Fade in={input.startAnimation}>
        <div className={classes.backdrop}>
          <div id='header-search-input' className={classes.inputContainer}>
            <IconButton onClick={closeSearchField}>
              <ArrowLeftIcon size={24} />
            </IconButton>
            <input
              ref={inputRef}
              className={classes.input}
              placeholder={input?.placeholder}
              onChange={onSearchChange}
            />
            <IconButton onClick={clearInput}>
              <TrashIcon size={20} />
            </IconButton>
          </div>
        </div>
      </Fade>
      <Fade in={!input.startAnimation}>{props.children}</Fade>
    </>
  );
};

export default withCourseTheme(SearchInputContainer);
